import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RETAIL_USER } from "src/constants/localStorage";
import { getAuthUser } from "src/services/Authentication/AuthenticationService";
import { unlock, logout, refreshTokens } from "src/store/Authentication/authActions";
import { RootState } from "src/store/reducers";

// Syncs auth state across browser tabs via the localStorage 'storage' event.
// The event only fires in tabs that did NOT make the change, so this only
// runs in the "other" tabs — exactly what we need.
const useStorageSync = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== RETAIL_USER) return;

      const newAuth = getAuthUser();

      // Another tab logged out
      if (!newAuth.logged || !newAuth.user) {
        dispatch(logout());
        return;
      }

      // Another tab unlocked the session (user entered password there)
      if (!newAuth.isLocked && auth.isLocked) {
        dispatch(unlock(newAuth.user));
        return;
      }

      // Another tab refreshed the token (proactive or 401 flow)
      // Reset this tab's timer by updating the jwtToken in Redux
      if (newAuth.user.jwtToken !== auth.user?.jwtToken) {
        dispatch(refreshTokens(newAuth.user));
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [dispatch, auth.isLocked, auth.user?.jwtToken]);
};

export default useStorageSync;
